<template>
  <div
    :data-provider-uuid="entityUuid"
    :data-provider-entity-type="entityType"
    :data-provider-entity-bundle="entityBundle"
    :data-blokkli-provider-active="isInEditor || void 0"
  >
    <BlokkliErrorBoundary v-if="isInEditor">
      <PreviewProvider
        v-if="isPreviewing"
        v-slot="{ mutatedEntity }"
        :entity="entity"
        :entity-type="entityType"
        :entity-uuid="entityUuid"
        :entity-bundle="entityBundle"
        :language="language"
      >
        <slot
          :entity="mutatedEntity"
          :is-editing="isEditing"
          :can-edit="canEdit"
          :is-preview="isPreviewing"
        />
      </PreviewProvider>
      <EditProvider
        v-else-if="isEditing && shouldRender"
        v-slot="{ mutatedEntity }"
        :entity="entity"
        :entity-type="entityType"
        :entity-uuid="entityUuid"
        :entity-bundle="entityBundle"
        :language="language"
        :isolate="isolate"
      >
        <slot
          :is-editing="isEditing"
          :can-edit="canEdit"
          :is-preview="isPreviewing"
          :entity="mutatedEntity"
        />
      </EditProvider>
    </BlokkliErrorBoundary>

    <slot
      v-else
      :is-editing="isEditing"
      :can-edit="canEdit"
      :is-preview="isPreviewing"
      :entity="entity"
    />

    <EditIndicator
      v-if="showIndicator"
      :uuid="entityUuid"
      :edit-label="editLabel"
      @edit="edit"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineAsyncComponent,
  useRoute,
  useRouter,
  provide,
  ref,
  onMounted
} from "#imports";
import {
  INJECT_ENTITY_CONTEXT,
  INJECT_PROVIDER_CONTEXT
} from "../helpers/symbols";
defineSlots();
const PreviewProvider = defineAsyncComponent(
  () => import("./Edit/PreviewProvider.vue")
);
const EditProvider = defineAsyncComponent(
  () => import("./Edit/EditProvider.vue")
);
const BlokkliErrorBoundary = defineAsyncComponent(
  () => import("./Edit/BlokkliErrorBoundary.vue")
);
const EditIndicator = defineAsyncComponent(
  () => import("./Edit/EditIndicator.vue")
);
const route = useRoute();
const router = useRouter();
const props = defineProps({
  entity: { type: null, required: false, default: void 0 },
  entityType: { type: String, required: true },
  entityBundle: { type: String, required: true },
  entityUuid: { type: String, required: true },
  canEdit: { type: Boolean, required: true },
  tag: { type: String, required: false, default: "div" },
  language: { type: String, required: false, default: "" },
  editLabel: { type: String, required: false, default: "" },
  editPath: { type: String, required: false, default: void 0 },
  isolate: { type: Boolean, required: false }
});
const shouldRender = ref(false);
const isInEditor = computed(
  () => props.entityUuid && props.entityType && props.entityBundle && (isPreviewing.value || isEditing.value)
);
const isEditing = computed(
  () => props.canEdit && !!props.entityUuid && route.query.blokkliEditing === props.entityUuid
);
const isPreviewing = computed(
  () => !!props.entityUuid && route.query.blokkliPreview === props.entityUuid
);
const showIndicator = computed(
  () => props.canEdit && !route.query.blokkliEditing && !route.query.blokkliPreview
);
function edit() {
  router.push({
    path: props.editPath || route.path,
    query: {
      blokkliEditing: props.entityUuid,
      language: props.language
    }
  });
}
const blokkliProviderEntityContext = computed(
  () => {
    return {
      uuid: props.entityUuid,
      type: props.entityType,
      bundle: props.entityBundle,
      language: props.language
    };
  }
);
provide(INJECT_PROVIDER_CONTEXT, blokkliProviderEntityContext);
provide(INJECT_ENTITY_CONTEXT, {
  uuid: props.entityUuid,
  type: props.entityType,
  bundle: props.entityBundle
});
onMounted(() => {
  shouldRender.value = true;
});
</script>
